import { AuthenticationResult, BrowserAuthError, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import { frontendSettings } from './api'

let jwtTokenPromise: Promise<AuthenticationResult | null> | null = null
let redirectInProgressPromise: Promise<void> | null = null
const debug: boolean = false

export async function getIdToken(): Promise<string> {
  const jwt = await getJwtToken()
  if (jwt) {
    return jwt.idToken
  }
  return ''
}

export async function getJwtToken(forceRefresh = false): Promise<AuthenticationResult | null> {
  // make sure we only have one promise at a time
  if (!jwtTokenPromise) {
    jwtTokenPromise = (async () => {
      try {
        return await internalGetJwtToken(forceRefresh)
      } finally {
        jwtTokenPromise = null
      }
    })()
  }
  return jwtTokenPromise
}

async function getFrontendSettings(): Promise<any> {
  try {
    return JSON.parse(localStorage.getItem('frontend_settings') || '')
  } catch (error) {
    return frontendSettings()
  }
}

async function internalGetJwtToken(forceRefresh = false): Promise<AuthenticationResult | null> {
  debug && console.log('internalGetJwtToken() - start')

  const settings = await getFrontendSettings()
  debug && console.log('internalGetJwtToken() - settings', settings)

  if (!settings || !settings.auth) {
    console.error('internalGetJwtToken() - Missing settings')
    throw new Error('Missing settings')
  }

  if (!settings.auth.enabled) {
    debug && console.log('internalGetJwtToken() - Auth disabled')
    return null
  }

  const msalInstance = await initializeMsalInstance(settings)

  const tokenRequest = { scopes: settings.auth.scopes }
  const redirectResult = await msalInstance.handleRedirectPromise()

  if (redirectResult) {
    return redirectResult
  }

  if (msalInstance.getAllAccounts().length === 0 || forceRefresh) {
    return loginUser(msalInstance, tokenRequest)
  } else {
    return refreshToken(msalInstance, tokenRequest)
  }
}

async function loginUser(msalInstance: PublicClientApplication, tokenRequest: any): Promise<AuthenticationResult | null> {
  await acquireTokenRedirectSafely(msalInstance, tokenRequest)
  throw new Error('Auth redirect initiated — request aborted')
}

async function refreshToken(msalInstance: PublicClientApplication, tokenRequest: any): Promise<AuthenticationResult | null> {
  debug && console.log('User already logged in')
  if (msalInstance.getActiveAccount() === null) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0])
  }

  try {
    const token = await msalInstance.acquireTokenSilent(tokenRequest)
    debug && console.log('Silent token:', token)
    return token
  } catch (error) {
    console.error('internalGetJwtToken() - Missing silent token', error)
    if (error instanceof InteractionRequiredAuthError) {
      await acquireTokenRedirectSafely(msalInstance, tokenRequest)
      throw new Error('Auth redirect initiated — request aborted')
    }
    throw new Error('Failed to acquire token silently')
  }
}

async function acquireTokenRedirectSafely(msalInstance: PublicClientApplication, tokenRequest: any): Promise<void> {
  if (redirectInProgressPromise) {
    return redirectInProgressPromise
  }

  redirectInProgressPromise = msalInstance
    .acquireTokenRedirect(tokenRequest)
    .catch(error => {
      if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
        debug && console.log('Auth redirect already in progress — treating as no-op')
        return
      }
      throw error
    })
    .finally(() => {
      redirectInProgressPromise = null
    })

  return redirectInProgressPromise
}

async function initializeMsalInstance(settings: any): Promise<PublicClientApplication> {
  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: settings.auth.client_id,
      authority: settings.auth.authority,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: true,
    },
  })

  await msalInstance.initialize()
  debug && console.log('MSAL instance:', msalInstance)
  return msalInstance
}
